// src/slices/aggregationsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAllKoncepts, getKapabilityData } from "../api/kentyouEye";

// Helper: numeric stats
function computeStats(values) {
  const nums = values.filter(v => typeof v === "number" && !Number.isNaN(v));
  if (nums.length === 0) {
    return { count: 0, avg: null, min: null, max: null };
  }
  const sum = nums.reduce((acc, v) => acc + v, 0);
  const avg = sum / nums.length;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  return { count: nums.length, avg, min, max };
}

// Async thunk to compute snapshot-level aggregations
export const fetchAggregations = createAsyncThunk(
  "aggregations/fetch",
  async (_, { rejectWithValue }) => {
    try {
      // 1) Get all koncepts
      const resp = await getAllKoncepts();
      const koncepts = resp.data || resp; // adapt to actual shape if needed

      const gateways = koncepts.filter(
        k => k.id && k.id.startsWith("esp32-")
      );
      const watches = koncepts.filter(
        k => k.id && k.id.startsWith("healthmonitor-")
      );
      const noahs = koncepts.filter(
        k => k.id && k.id.startsWith("noah-")
      );

      // 2) For all watches, fetch health-related metrics
      const stepsValues = [];
      const heartRateValues = [];
      const oxymetryValues = [];
      const batteryValues = [];

      for (const w of watches) {
        const id = w.id;

        // In parallel per watch
        const [stepsRes, hrRes, oxyRes, batRes] = await Promise.all([
          getKapabilityData(id, "steps~data").catch(() => null),
          getKapabilityData(id, "heartrate~data").catch(() => null),
          getKapabilityData(id, "oxymetry~data").catch(() => null),
          getKapabilityData(id, "batterylevel~data").catch(() => null),
        ]);

        if (stepsRes?.data?.value != null) {
          stepsValues.push(Number(stepsRes.data.value));
        }
        if (hrRes?.data?.value != null) {
          heartRateValues.push(Number(hrRes.data.value));
        }
        if (oxyRes?.data?.value != null) {
          oxymetryValues.push(Number(oxyRes.data.value));
        }
        if (batRes?.data?.value != null) {
          batteryValues.push(Number(batRes.data.value));
        }
      }

      // 3) Compute stats
      const stepsStats = computeStats(stepsValues);
      const heartRateStats = computeStats(heartRateValues);
      const oxymetryStatsBase = computeStats(oxymetryValues);
      const batteryStatsBase = computeStats(batteryValues);

      // 4) Additional counts for health flags
      const lowSpO2Threshold = 92;
      const highHeartRateThreshold = 100;
      const lowBatteryThreshold = 30;
      const midBatteryThreshold = 60;

      const lowSpO2Count = oxymetryValues.filter(
        v => typeof v === "number" && v < lowSpO2Threshold
      ).length;

      const highHeartRateCount = heartRateValues.filter(
        v => typeof v === "number" && v > highHeartRateThreshold
      ).length;

      const lowBatteryCount = batteryValues.filter(
        v => typeof v === "number" && v < lowBatteryThreshold
      ).length;

      const midBatteryCount = batteryValues.filter(
        v =>
          typeof v === "number" &&
          v >= lowBatteryThreshold &&
          v <= midBatteryThreshold
      ).length;

      const highBatteryCount = batteryValues.filter(
        v => typeof v === "number" && v > midBatteryThreshold
      ).length;

      return {
        snapshotTime: new Date().toISOString(),
        deviceCounts: {
          total: koncepts.length,
          gateways: gateways.length,
          watches: watches.length,
          noah: noahs.length,
        },
        stepsStats,
        heartRateStats: {
          ...heartRateStats,
          highHeartRateCount,
          highHeartRateThreshold,
        },
        oxymetryStats: {
          ...oxymetryStatsBase,
          lowSpO2Count,
          lowSpO2Threshold,
        },
        batteryStats: {
          ...batteryStatsBase,
          lowBatteryCount,
          midBatteryCount,
          highBatteryCount,
          lowBatteryThreshold,
          midBatteryThreshold,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch aggregations");
    }
  }
);

const aggregationsSlice = createSlice({
  name: "aggregations",
  initialState: {
    status: "idle", // idle | loading | succeeded | failed
    error: null,
    snapshotTime: null,
    deviceCounts: null,
    stepsStats: null,
    heartRateStats: null,
    oxymetryStats: null,
    batteryStats: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAggregations.pending, state => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAggregations.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;

        state.snapshotTime = action.payload.snapshotTime;
        state.deviceCounts = action.payload.deviceCounts;
        state.stepsStats = action.payload.stepsStats;
        state.heartRateStats = action.payload.heartRateStats;
        state.oxymetryStats = action.payload.oxymetryStats;
        state.batteryStats = action.payload.batteryStats;
      })
      .addCase(fetchAggregations.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Unknown error";
      });
  },
});

export default aggregationsSlice.reducer;

// Optional selectors
export const selectAggregations = state => state.aggregations;
